import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../../components/ui/dialog';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import {
  Upload, FileText, Sparkles, AlertTriangle, Loader2, ArrowRight, ArrowLeft,
  CheckCircle2, X,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const fmtZar = (n) =>
  `R${Number(n || 0).toLocaleString('en-ZA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

const STEP = { TRADE: 0, UPLOAD: 1, ANALYZING: 2, REVIEW: 3 };

const ACCEPT_TYPES = '.pdf,.png,.jpg,.jpeg,.webp,application/pdf,image/png,image/jpeg,image/webp';

// ---------------------------------------------------------------------------
// NewQuoteWizard — 4-step wizard for Phase 1B:
//   1. Pick trade + title + (optional) client name
//   2. Upload plan files (1-6) + optional notes for the AI
//   3. AI analyses with Gemini 3 Pro vision
//   4. Review suggestions → save as draft quote
// ---------------------------------------------------------------------------
const NewQuoteWizard = ({ open, onClose, trades, onCreated }) => {
  const [step, setStep] = useState(STEP.TRADE);
  const [title, setTitle] = useState('Untitled quote');
  const [clientName, setClientName] = useState('');
  const [trade, setTrade] = useState('painter');
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState('');
  const [analysis, setAnalysis] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
  });

  useEffect(() => {
    if (!open) {
      // Reset state on close
      setStep(STEP.TRADE);
      setTitle('Untitled quote');
      setClientName('');
      setTrade('painter');
      setFiles([]);
      setNotes('');
      setAnalysis(null);
      setError(null);
      setBusy(false);
    }
  }, [open]);

  const addFiles = (incoming) => {
    const arr = Array.from(incoming || []);
    const next = [...files, ...arr].slice(0, 6);
    setFiles(next);
  };

  const removeFile = (idx) => {
    setFiles(files.filter((_, i) => i !== idx));
  };

  const runAnalysis = async () => {
    setStep(STEP.ANALYZING);
    setBusy(true);
    setError(null);
    try {
      const fd = new FormData();
      fd.append('trade', trade);
      fd.append('notes', notes || '');
      files.forEach((f) => fd.append('files', f));
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/quotes/analyze-plan`,
        fd,
        { headers: { ...authHeaders(), 'Content-Type': 'multipart/form-data' }, timeout: 240000 },
      );
      setAnalysis(res.data);
      setStep(STEP.REVIEW);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setError(msg);
      setStep(STEP.UPLOAD);
      toast.error('Analysis failed');
    } finally {
      setBusy(false);
    }
  };

  const saveDraft = async () => {
    if (!analysis) return;
    setBusy(true);
    try {
      const payload = {
        title: title || 'Untitled quote',
        client_name: clientName || null,
        trade,
        source_files: analysis.source_files || [],
        extracted_data: {
          plan_summary: analysis.plan_summary,
          measurements: analysis.measurements,
          missing_items: analysis.missing_items,
          confidence: analysis.confidence,
          notes,
        },
        material_lines: analysis.material_lines.map((l) => ({
          material_rate_id: l.material_rate_id,
          name: l.name,
          unit: l.unit,
          qty: l.qty,
          unit_price_zar: l.unit_price_zar,
        })),
        labor_lines: analysis.labor_lines.map((l) => ({
          labor_rate_id: l.labor_rate_id,
          role: l.role,
          hours: l.hours,
          hourly_rate_zar: l.hourly_rate_zar,
        })),
        vat_rate: 0.15,
      };
      const res = await axios.post(
        `${BACKEND_URL}/api/admin/quotes`,
        payload,
        { headers: authHeaders() },
      );
      toast.success('Draft quote saved');
      onCreated && onCreated(res.data);
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Save failed');
    } finally {
      setBusy(false);
    }
  };

  const canContinueFromTrade = title.trim().length > 0 && !!trade;
  const canContinueFromUpload = files.length > 0 && !busy;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && !busy && onClose()}>
      <DialogContent className="max-w-3xl" data-testid="admin-quote-wizard">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="h-5 w-5" style={{ color: '#00CED1' }} />
            New AI-assisted quote
          </DialogTitle>
          <DialogDescription>
            Upload a building plan, let Gemini 3 Pro analyse it, then save the suggestions as a draft quote you can edit.
          </DialogDescription>
        </DialogHeader>

        {/* Step indicator */}
        <div className="flex items-center gap-2 text-xs mb-2" data-testid="admin-quote-wizard-stepper">
          <StepDot active={step === STEP.TRADE} done={step > STEP.TRADE} label="1 · Trade" />
          <StepLine />
          <StepDot active={step === STEP.UPLOAD} done={step > STEP.UPLOAD} label="2 · Upload" />
          <StepLine />
          <StepDot active={step === STEP.ANALYZING || step === STEP.REVIEW} done={step > STEP.REVIEW} label="3 · Analyse" />
          <StepLine />
          <StepDot active={step === STEP.REVIEW} done={false} label="4 · Review" />
        </div>

        {/* ----- Step 1: Trade ----- */}
        {step === STEP.TRADE && (
          <div className="space-y-4" data-testid="admin-quote-wizard-step-trade">
            <div>
              <Label className="text-xs">Quote title</Label>
              <Input
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Smith residence — interior repaint"
                data-testid="admin-quote-wizard-title"
              />
            </div>
            <div>
              <Label className="text-xs">Client name (optional)</Label>
              <Input
                value={clientName}
                onChange={(e) => setClientName(e.target.value)}
                placeholder="e.g. Mr & Mrs Smith"
                data-testid="admin-quote-wizard-client"
              />
            </div>
            <div>
              <Label className="text-xs">Trade</Label>
              <Select value={trade} onValueChange={setTrade}>
                <SelectTrigger data-testid="admin-quote-wizard-trade">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {trades.map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-gray-500 mt-1.5">
                The AI uses your rate library for this trade to price the quote.
              </p>
            </div>
          </div>
        )}

        {/* ----- Step 2: Upload ----- */}
        {step === STEP.UPLOAD && (
          <div className="space-y-4" data-testid="admin-quote-wizard-step-upload">
            <div
              className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors hover:border-teal-400 hover:bg-teal-50/40"
              onClick={() => fileInputRef.current?.click()}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault();
                addFiles(e.dataTransfer.files);
              }}
              data-testid="admin-quote-wizard-dropzone"
            >
              <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
              <p className="text-sm font-semibold text-gray-700">Drop plans here or click to browse</p>
              <p className="text-xs text-gray-500 mt-1">PDF / PNG / JPG / WEBP · up to 6 files · 25 MB each</p>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept={ACCEPT_TYPES}
                className="hidden"
                onChange={(e) => addFiles(e.target.files)}
                data-testid="admin-quote-wizard-file-input"
              />
            </div>

            {files.length > 0 && (
              <div className="space-y-1.5" data-testid="admin-quote-wizard-file-list">
                {files.map((f, i) => (
                  <div
                    key={`${f.name}-${i}`}
                    className="flex items-center justify-between bg-gray-50 rounded px-3 py-2 text-sm"
                  >
                    <div className="flex items-center gap-2 truncate">
                      <FileText className="h-4 w-4 text-gray-500 flex-shrink-0" />
                      <span className="truncate">{f.name}</span>
                      <span className="text-xs text-gray-400 flex-shrink-0">
                        {(f.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => removeFile(i)}
                      className="text-gray-400 hover:text-red-600 p-1"
                      data-testid={`admin-quote-wizard-remove-file-${i}`}
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div>
              <Label className="text-xs">Optional notes for the AI</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g. Wall height is 2.7m. Skip the ceilings. Only paint Bedrooms 1 & 2."
                rows={3}
                data-testid="admin-quote-wizard-notes"
              />
            </div>

            {error && (
              <div
                className="border border-red-300 bg-red-50 rounded-md p-3 text-xs text-red-700 flex items-start gap-2"
                data-testid="admin-quote-wizard-error"
              >
                <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                <span className="break-words">{error}</span>
              </div>
            )}
          </div>
        )}

        {/* ----- Step 3: Analysing ----- */}
        {step === STEP.ANALYZING && (
          <div className="py-12 text-center space-y-3" data-testid="admin-quote-wizard-step-analyzing">
            <Loader2 className="h-10 w-10 mx-auto animate-spin" style={{ color: '#00CED1' }} />
            <p className="text-sm font-semibold text-gray-700">Gemini 3 Pro is reading your plan…</p>
            <p className="text-xs text-gray-500">This usually takes 20-60 seconds depending on file size.</p>
          </div>
        )}

        {/* ----- Step 4: Review ----- */}
        {step === STEP.REVIEW && analysis && (
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-1" data-testid="admin-quote-wizard-step-review">
            <ReviewBlock analysis={analysis} />
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          {step === STEP.TRADE && (
            <>
              <Button variant="outline" onClick={onClose}>Cancel</Button>
              <Button
                onClick={() => setStep(STEP.UPLOAD)}
                disabled={!canContinueFromTrade}
                className="text-white font-semibold"
                style={{ backgroundColor: '#00CED1' }}
                data-testid="admin-quote-wizard-next-from-trade"
              >
                Next <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </>
          )}
          {step === STEP.UPLOAD && (
            <>
              <Button variant="outline" onClick={() => setStep(STEP.TRADE)} disabled={busy}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
              <Button
                onClick={runAnalysis}
                disabled={!canContinueFromUpload}
                className="text-white font-semibold"
                style={{ backgroundColor: '#00CED1' }}
                data-testid="admin-quote-wizard-analyze"
              >
                <Sparkles className="h-4 w-4 mr-2" /> Analyse plan
              </Button>
            </>
          )}
          {step === STEP.REVIEW && (
            <>
              <Button variant="outline" onClick={() => setStep(STEP.UPLOAD)} disabled={busy}>
                <ArrowLeft className="h-4 w-4 mr-2" /> Re-upload
              </Button>
              <Button
                onClick={saveDraft}
                disabled={busy}
                className="text-white font-semibold"
                style={{ backgroundColor: '#00CED1' }}
                data-testid="admin-quote-wizard-save-draft"
              >
                {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Save as draft
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const StepDot = ({ active, done, label }) => (
  <span
    className={`flex items-center gap-1 font-semibold ${
      active ? 'text-teal-600' : done ? 'text-green-600' : 'text-gray-400'
    }`}
  >
    <span
      className={`h-2 w-2 rounded-full ${active ? 'bg-teal-500' : done ? 'bg-green-500' : 'bg-gray-300'}`}
    />
    {label}
  </span>
);

const StepLine = () => <span className="flex-grow h-px bg-gray-200 max-w-[2rem]" />;

const ReviewBlock = ({ analysis }) => {
  const conf = (analysis.confidence || 'medium').toLowerCase();
  const confColor =
    conf === 'high' ? 'rgb(22 163 74)' :
    conf === 'low' ? 'rgb(220 38 38)' : 'rgb(217 119 6)';
  const measurements = analysis.measurements || {};
  const rooms = measurements.rooms || [];
  const preview = analysis.preview || {};

  return (
    <>
      <div className="flex items-center gap-2 flex-wrap">
        <Badge
          variant="outline"
          style={{ color: confColor, borderColor: confColor }}
          data-testid="admin-quote-wizard-confidence"
        >
          Confidence: {conf.toUpperCase()}
        </Badge>
        {analysis.source_files?.length > 0 && (
          <span className="text-xs text-gray-500">
            {analysis.source_files.length} plan file{analysis.source_files.length === 1 ? '' : 's'} stored
          </span>
        )}
      </div>

      {analysis.plan_summary && (
        <div className="bg-gray-50 border rounded-md p-3 text-sm text-gray-700">
          <p className="font-semibold mb-1 text-xs uppercase tracking-wide text-gray-500">Plan summary</p>
          {analysis.plan_summary}
        </div>
      )}

      {rooms.length > 0 && (
        <div data-testid="admin-quote-wizard-rooms">
          <p className="font-semibold mb-2 text-xs uppercase tracking-wide text-gray-500">Rooms detected ({rooms.length})</p>
          <div className="overflow-x-auto border rounded-md">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1.5 text-left">Room</th>
                  <th className="px-2 py-1.5 text-right">L × W (m)</th>
                  <th className="px-2 py-1.5 text-right">Floor m²</th>
                  <th className="px-2 py-1.5 text-right">Wall m²</th>
                </tr>
              </thead>
              <tbody>
                {rooms.map((r, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1.5">{r.name}</td>
                    <td className="px-2 py-1.5 text-right">{r.length_m} × {r.width_m}</td>
                    <td className="px-2 py-1.5 text-right">{r.floor_area_m2}</td>
                    <td className="px-2 py-1.5 text-right">{r.wall_area_m2}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {analysis.material_lines?.length > 0 && (
        <div data-testid="admin-quote-wizard-materials">
          <p className="font-semibold mb-2 text-xs uppercase tracking-wide text-gray-500">
            Material lines ({analysis.material_lines.length})
          </p>
          <div className="overflow-x-auto border rounded-md">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1.5 text-left">Item</th>
                  <th className="px-2 py-1.5 text-right">Qty</th>
                  <th className="px-2 py-1.5 text-right">Unit</th>
                  <th className="px-2 py-1.5 text-right">Unit price</th>
                  <th className="px-2 py-1.5 text-right">Line total</th>
                </tr>
              </thead>
              <tbody>
                {analysis.material_lines.map((l, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1.5">
                      <div>{l.name}</div>
                      {l.note && <div className="text-[10px] text-gray-500">{l.note}</div>}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{l.qty}</td>
                    <td className="px-2 py-1.5 text-right">{l.unit}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtZar(l.unit_price_zar)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold">{fmtZar(l.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {analysis.labor_lines?.length > 0 && (
        <div data-testid="admin-quote-wizard-labor">
          <p className="font-semibold mb-2 text-xs uppercase tracking-wide text-gray-500">
            Labor lines ({analysis.labor_lines.length})
          </p>
          <div className="overflow-x-auto border rounded-md">
            <table className="w-full text-xs">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-2 py-1.5 text-left">Role</th>
                  <th className="px-2 py-1.5 text-right">Hours</th>
                  <th className="px-2 py-1.5 text-right">Hourly</th>
                  <th className="px-2 py-1.5 text-right">Line total</th>
                </tr>
              </thead>
              <tbody>
                {analysis.labor_lines.map((l, i) => (
                  <tr key={i} className="border-t">
                    <td className="px-2 py-1.5">
                      <div>{l.role}</div>
                      {l.note && <div className="text-[10px] text-gray-500">{l.note}</div>}
                    </td>
                    <td className="px-2 py-1.5 text-right font-mono">{l.hours}</td>
                    <td className="px-2 py-1.5 text-right font-mono">{fmtZar(l.hourly_rate_zar)}</td>
                    <td className="px-2 py-1.5 text-right font-mono font-semibold">{fmtZar(l.line_total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {analysis.missing_items?.length > 0 && (
        <div
          className="border border-amber-300 bg-amber-50 rounded-md p-3 text-xs text-amber-800"
          data-testid="admin-quote-wizard-missing"
        >
          <p className="font-semibold mb-1">
            Items the AI saw but couldn't price (not in your rate library):
          </p>
          <ul className="list-disc list-inside space-y-0.5">
            {analysis.missing_items.map((m, i) => <li key={i}>{m}</li>)}
          </ul>
        </div>
      )}

      {/* Preview totals */}
      <div className="bg-teal-50 border border-teal-200 rounded-md p-4" data-testid="admin-quote-wizard-totals">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
          <Totals label="Materials" value={preview.materials_total} />
          <Totals label="Labor" value={preview.labor_total} />
          <Totals label="VAT (15%)" value={preview.vat_amount} />
          <Totals label="Total" value={preview.total} bold />
        </div>
      </div>
    </>
  );
};

const Totals = ({ label, value, bold }) => (
  <div>
    <p className="text-[10px] uppercase tracking-wide text-teal-700">{label}</p>
    <p className={`font-mono ${bold ? 'text-lg font-bold' : ''}`}>{fmtZar(value)}</p>
  </div>
);

export default NewQuoteWizard;
